import React, { useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Box } from '@abyss/web/ui/Box';
import { Label } from '@abyss/web/ui/Label';
import { Button } from '@abyss/web/ui/Button';
import { Link } from '@abyss/web/ui/Link';
import { Grid } from '@abyss/web/ui/Grid';
import { Card } from '@abyss/web/ui/Card';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Layout } from '@abyss/web/ui/Layout';
import { Heading } from '@abyss/web/ui/Heading';
import { useForm } from '@abyss/web/hooks/useForm';
import { FormProvider } from '@abyss/web/ui/FormProvider';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { PageTitleService } from '@src/api/common/PageTitleService';
import { BreadcrumbService } from '@src/api/common/BreadcrumbService';
import { API_URL } from '@src/constants/BaseUrls';
import { CookieService } from '@src/api/common/CookieService';
import { RouteToolStepper } from '../Stepper';
import {
  appendRouteToolHistoryRecord,
  createHistoryRecordFromUpload,
} from '../RouteToolHistoryStore';
import { Breadcrumbs, Title } from './PageInfo';

interface FileUploadState {
  file: File | null;
  error: string;
  uploadedFileName?: string;
}

type UploadFileKind = 'clinic' | 'warehouse' | 'remoteUser';

type PersistedUploadState = {
  entityName: string;
  clinicFileName?: string;
  warehouseFileName?: string;
  remoteUserFileName?: string;
};

const UPLOAD_STATE_STORAGE_KEY = 'routeToolUploadState';
const ROUTE_TOOL_UPLOAD_FLAGS_KEY = 'routeToolUploadFlags';
const UPLOAD_PROCESSING_KEY = 'routeToolUploadProcessing';

interface EntityOption {
  value: string;
  label: string;
}

const normalizeBaseUrl = (baseUrl?: string) => {
  if (!baseUrl) {
    return '';
  }
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
};

const resolveRouteToolApiBase = () => {
  const configured = normalizeBaseUrl(API_URL);
  if (!configured || configured.includes('xyz-dev.com')) {
    return 'http://localhost:5000';
  }
  return configured;
};

const buildApiUrl = (baseUrl: string, path: string) => {
  if (baseUrl.endsWith('/api') && path.startsWith('/api/')) {
    return `${baseUrl}${path.slice(4)}`;
  }
  return `${baseUrl}${path}`;
};

interface FileBoxProps {
  label: string;
  required?: boolean;
  fileState: FileUploadState;
  onFileChange: (file: File | null) => Promise<void>;
  fileKind: UploadFileKind;
  onDownloadTemplate: (fileKind: UploadFileKind) => void;
  onPreview: (fileKind: UploadFileKind) => Promise<{
    headers: string[];
    rows: string[][];
  }>;
  /** Amber warning shown when the file has rows with missing address fields. */
  warning?: string;
}

const parseCsvRow = (line: string): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    const nextCharacter = line[index + 1];

    if (character === '"') {
      if (inQuotes && nextCharacter === '"') {
        currentValue += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (character === ',' && !inQuotes) {
      values.push(currentValue.trim());
      currentValue = '';
      continue;
    }

    currentValue += character;
  }

  values.push(currentValue.trim());
  return values.map((value) => value.replace(/^"|"$/g, '').trim());
};

const validateUploadFile = async (file: File): Promise<string | null> => {
  const name = file.name.toLowerCase();

  // Only Excel files allowed
  if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
    return 'File must be an Excel file (.xlsx or .xls).';
  }

  if (!/^[a-zA-Z0-9._\-\s]+$/.test(file.name)) {
    return 'File name contains unsupported characters.';
  }

  // Check file size
  const maxBytes = 50 * 1024 * 1024;
  if (file.size > maxBytes) {
    return 'File exceeds the 50 MB size limit.';
  }

  // Check if file is empty
  if (file.size === 0) {
    return 'File is empty. Please select a valid Excel file with data.';
  }

  return null;
};

const hasAnyKeyword = (name: string, keywords: string[]) =>
  keywords.some((keyword) => name.includes(keyword));

const validateFileKindByName = (
  file: File,
  expectedKind: UploadFileKind
): string | null => {
  const name = file.name.toLowerCase();

  const keywordMap: Record<UploadFileKind, string[]> = {
    clinic: ['clinic', 'site', 'general'],
    warehouse: ['warehouse'],
    remoteUser: ['user', 'remote', 'employee'],
  };

  const matchesExpected = hasAnyKeyword(name, keywordMap[expectedKind]);
  if (!matchesExpected) {
    const kindLabel =
      expectedKind === 'clinic'
        ? 'Clinic Site'
        : expectedKind === 'warehouse'
          ? 'Warehouse'
          : 'Remote User';

    return `${kindLabel} file appears incorrect. Please choose the correct file.`;
  }

  const mismatchedByOtherKind =
    expectedKind !== 'clinic' && hasAnyKeyword(name, keywordMap.clinic)
      ? 'clinic'
      : expectedKind !== 'warehouse' &&
          hasAnyKeyword(name, keywordMap.warehouse)
        ? 'warehouse'
        : expectedKind !== 'remoteUser' &&
            hasAnyKeyword(name, keywordMap.remoteUser)
          ? 'remoteUser'
          : null;

  if (mismatchedByOtherKind) {
    const expectedLabel =
      expectedKind === 'clinic'
        ? 'Clinic Site'
        : expectedKind === 'warehouse'
          ? 'Warehouse'
          : 'Remote User';
    return `Selected file does not match ${expectedLabel}. Please select the right file.`;
  }

  return null;
};

/**
 * Reads an Excel file and returns a warning message when rows have missing
 * address fields. Returns null when addresses look complete or the file
 * cannot be parsed.
 */
const checkAddressMissing = async (file: File | null): Promise<string | null> => {
  if (!file) return null;
  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) return null;
    const sheet = workbook.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, defval: '' }) as string[][];
    if (data.length < 2) return null;

    const headers = data[0].map((h) => String(h ?? '').toLowerCase().trim());
    const addressKeywords = ['address', 'street', 'addr'];
    const addressColIndices = headers
      .map((h, i) => ({ h, i }))
      .filter(({ h }) => addressKeywords.some((kw) => h.includes(kw)))
      .map(({ i }) => i);

    if (addressColIndices.length === 0) return null;

    const missingRowNumbers: number[] = [];
    data.slice(1).forEach((row, idx) => {
      if (!row.some((cell) => String(cell ?? '').trim())) return;
      if (addressColIndices.some((colIdx) => !String(row[colIdx] ?? '').trim())) {
        missingRowNumbers.push(idx + 2); // +1 for header row, +1 for 1-based index
      }
    });

    if (missingRowNumbers.length === 0) return null;

    const MAX_SHOWN = 5;
    const shown = missingRowNumbers.slice(0, MAX_SHOWN);
    const remaining = missingRowNumbers.length - shown.length;
    const rowLabel = missingRowNumbers.length > 1 ? 'Rows' : 'Row';
    const verbLabel = missingRowNumbers.length > 1 ? 'have' : 'has';
    const rowList = remaining > 0
      ? `${shown.join(', ')} and ${remaining} more`
      : shown.join(', ');

    return `${file.name} — ${rowLabel} ${rowList} ${verbLabel} missing address fields. Upload may take longer to process.`;
  } catch {
    return null;
  }
};

const FileUploadBox = ({
  label,
  required,
  fileState,
  onFileChange,
  fileKind,
  onDownloadTemplate,
  onPreview,
  warning,
}: FileBoxProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHeaders, setPreviewHeaders] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<string[][]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const hasUploadedFile = Boolean(fileState.file || fileState.uploadedFileName);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const buildPreviewFromFile = async (file: File | null) => {
    if (!file) {
      setPreviewHeaders([]);
      setPreviewRows([]);
      setShowPreview(false);
      return;
    }

    // Excel file preview is not supported, but file is still valid for upload
    // The backend will validate the content
    const lowerFileName = file.name.toLowerCase();
    if (lowerFileName.endsWith('.xlsx') || lowerFileName.endsWith('.xls')) {
      // Don't auto-show preview on select; user clicks Preview button to trigger it
      setPreviewHeaders([]);
      setPreviewRows([]);
      setShowPreview(false);
      return;
    }

    // CSV preview logic (kept for backwards compatibility)
    if (!lowerFileName.endsWith('.csv')) {
      setPreviewHeaders([]);
      setPreviewRows([]);
      setShowPreview(false);
      return;
    }

    const text = await file.text();
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (lines.length === 0) {
      setPreviewHeaders([]);
      setPreviewRows([]);
      setShowPreview(false);
      return;
    }

    const headers = parseCsvRow(lines[0]);
    const rows = lines.slice(1, 6).map((line) => parseCsvRow(line));
    setPreviewHeaders(headers);
    setPreviewRows(rows);
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] || null;
    await onFileChange(selected);
    await buildPreviewFromFile(selected);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    void onFileChange(null);
    setPreviewHeaders([]);
    setPreviewRows([]);
    setShowPreview(false);
    setPreviewError('');
    setPreviewLoading(false);
  };

  const handlePreviewClick = async () => {
    if (!hasUploadedFile) {
      return;
    }

    setShowPreview(true);
    setPreviewLoading(true);
    setPreviewError('');
    setPreviewHeaders([]);
    setPreviewRows([]);

    try {
      const preview = await onPreview(fileKind);
      setPreviewHeaders(preview.headers);
      setPreviewRows(preview.rows);

      if (!preview.headers.length) {
        setPreviewError('Preview is not available for this file yet.');
      }
    } catch (error: any) {
      setPreviewHeaders([]);
      setPreviewRows([]);
      setPreviewError(error?.message || 'Unable to load preview.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const selected = e.dataTransfer.files?.[0] || null;
    await onFileChange(selected);
    await buildPreviewFromFile(selected);
  };

  const displayError = fileState.error;

  return (
    <Card
      css={{
        width: '100%',
        background: '#ffffff',
        border: '1px solid #d9e0eb',
        boxShadow: '0 2px 8px rgba(20, 40, 80, 0.06)',
      }}
    >
      <Card.Section css={{ padding: 0 }}>
        <Layout.Stack space={0} css={{ alignItems: 'stretch', width: '100%' }}>
          <Box
            css={{
              padding: '10px 14px 8px 14px',
              backgroundColor: '#ffffff',
              width: '100%',
            }}
          >
            <Layout.Group
              alignItems="center"
              space={6}
              css={{
                justifyContent: 'flex-start',
                width: '100%',
                marginBottom: '8px',
              }}
            >
              <Label
                fontWeight="bold"
                size="md"
                color="$gray8"
                css={{ whiteSpace: 'nowrap' }}
              >
                {label}
              </Label>
              {required && (
                <Label size="sm" color="$error1">
                  *
                </Label>
              )}
            </Layout.Group>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                width: '100%',
                marginTop: '10px',
                flexWrap: 'nowrap',
              }}
            >
              <Button
                size="$sm"
                onClick={handleClick}
                css={{ padding: '4px 8px', fontSize: '12px', minWidth: 0, height: '28px', gap: '4px' }}
              >
                <IconSymbol icon="upload_file" size={12} />
                Choose
              </Button>
              <Button
                size="$sm"
                variant="outline"
                onClick={handleRemove}
                isDisabled={!hasUploadedFile}
                css={{ padding: '4px 8px', fontSize: '12px', minWidth: 0, height: '28px', gap: '4px' }}
              >
                <IconSymbol icon="cancel" size={12} />
                Cancel
              </Button>
              {hasUploadedFile && (
                <Button
                  size="$sm"
                  variant="outline"
                  onClick={handlePreviewClick}
                  css={{ padding: '4px 8px', fontSize: '12px', minWidth: 0, height: '28px', gap: '4px' }}
                >
                  <IconSymbol icon="visibility" size={12} />
                  Preview
                </Button>
              )}
            </div>
          </Box>

          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            css={{
              border: `2px dashed ${isDragOver ? '#8e8e8e' : '#b8b8b8'}`,
              borderRadius: '8px',
              backgroundColor: hasUploadedFile ? '#e8f5e9' : '#f5f5f5',
              minHeight: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '10px',
              width: 'calc(100% - 32px)',
              height: '100%',
              marginLeft: '16px',
              marginRight: '16px',
              position: 'relative',
              padding: '16px',
            }}
          >
            <Box
              css={{
                flex: 1,
                width: 'calc(100% + 16px)',
                marginRight: '-16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
              }}
            >
              {!hasUploadedFile && (
                <Box
                  css={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: '#eef4ff',
                    border: '1px solid #c7d8f7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  <IconSymbol icon="cloud_upload" size={26} color="$primary1" />
                </Box>
              )}
              {hasUploadedFile && (
                <Box
                  css={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: '#d4f4dd',
                    border: '2px solid #52b788',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconSymbol icon="check_circle" size={32} color="$success1" />
                </Box>
              )}

              <Label size="sm" color="$gray6" css={{ textAlign: 'center' }}>
                {hasUploadedFile
                  ? 'File uploaded successfully'
                  : 'Drag and drop files to here to upload.'}
              </Label>
              {fileState.uploadedFileName ? (
                <Label size="xs" color="$gray6" css={{ textAlign: 'center' }}>
                  {fileState.uploadedFileName}
                </Label>
              ) : null}
            </Box>
          </Box>

          {showPreview && (
            <Box
              css={{
                position: 'fixed',
                inset: 0,
                zIndex: 1100,
                backgroundColor: 'rgba(0, 0, 0, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
              }}
              onClick={() => setShowPreview(false)}
            >
              <Box
                css={{
                  width: 'min(96vw, 1100px)',
                  maxWidth: '96vw',
                  maxHeight: '80vh',
                  height: 'auto',
                  overflow: 'hidden',
                  backgroundColor: '#ffffff',
                  borderRadius: '10px',
                  boxShadow: '0 10px 28px rgba(0, 0, 0, 0.22)',
                  border: '1px solid #d9e0eb',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Layout.Group
                  alignItems="center"
                  css={{
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderBottom: '1px solid #e7e7e7',
                  }}
                >
                  <Label fontWeight="bold" size="md" color="$gray8">
                    File Preview
                  </Label>
                  <Button size="$sm" variant="outline" onClick={() => setShowPreview(false)}>
                    <IconSymbol icon="close" size={14} />
                    Close
                  </Button>
                </Layout.Group>

                <Box
                  css={{
                    padding: '10px 14px 10px',
                    backgroundColor: '#ffffff',
                    width: '100%',
                    height: 'auto',
                    minHeight: 'unset',
                    maxHeight: '80vh',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                  }}
                >
                  {previewLoading ? (
                    <Box
                      css={{
                        width: '100%',
                        minHeight: '190px',
                        padding: '12px 14px',
                        border: '1px solid #e3e7ee',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        marginBottom: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        css={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <svg
                          aria-label="Loading preview"
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          role="img"
                        >
                          <circle cx="12" cy="12" r="9" fill="none" stroke="#d9dee8" strokeWidth="3" />
                          <path d="M12 3a9 9 0 0 1 9 9" fill="none" stroke="#2f5da8" strokeWidth="3" strokeLinecap="round">
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              from="0 12 12"
                              to="360 12 12"
                              dur="0.8s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </svg>
                        <Label size="sm" color="$gray7" fontWeight="bold">
                          Loading preview...
                        </Label>
                      </Box>
                    </Box>
                  ) : previewError ? (
                    <Box
                      css={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #f1c6c6',
                        borderRadius: '8px',
                        backgroundColor: '#fff5f5',
                        marginBottom: previewHeaders.length > 0 ? '10px' : '0',
                      }}
                    >
                      <Label size="sm" color="$error1">
                        {previewError}
                      </Label>
                    </Box>
                  ) : previewHeaders.length > 0 ? (
                    <Box css={{ width: '100%', overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 0 }}>
                        <thead>
                          <tr style={{ backgroundColor: '#ffffff' }}>
                            {previewHeaders.map((col) => (
                              <th
                                key={col}
                                style={{
                                  padding: '8px 12px',
                                  textAlign: 'left',
                                  fontSize: '13px',
                                  fontWeight: 700,
                                  color: '#2d3a4a',
                                  borderBottom: '1px solid #e0e6ef',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewRows.map((row, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #edf1f7' }}>
                              {previewHeaders.map((_, cellIdx) => (
                                <td
                                  key={cellIdx}
                                  style={{
                                    padding: '10px 12px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#2f3f55',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {row[cellIdx] || ''}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  ) : (
                    <Label size="sm" color="$gray6">
                      Preview has no rows to display.
                    </Label>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          <Box
            css={{
              padding: '6px 14px 8px 14px',
              backgroundColor: '#ffffff',
              width: '100%',
            }}
          >
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onDownloadTemplate(fileKind);
              }}
              css={{
                fontSize: '13px',
                fontWeight: 600,
                color: '$primary1',
                textDecoration: 'underline',
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <IconSymbol icon="download" size={13} />
              Download Template
            </Link>
            <Label
              size="xs"
              color="$gray5"
              css={{ display: 'block', marginTop: '4px' }}
            >
              Accepted: XLSX / XLS &nbsp;&middot;&nbsp; Max size: 50 MB
            </Label>
            {displayError && (
              <Label size="sm" color="$error1" css={{ display: 'block', marginTop: '4px' }}>
                {displayError}
              </Label>
            )}
            {warning && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginTop: '6px', padding: '6px 8px', backgroundColor: '#fffbeb', borderRadius: '4px', border: '1px solid #fbbf24' }}>
                <span style={{ fontSize: '13px', flexShrink: 0 }}>⚠️</span>
                <span style={{ fontSize: '12px', color: '#92400e', fontWeight: 500, lineHeight: 1.4 }}>{warning}</span>
              </div>
            )}
          </Box>

          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={(e) => {
              void handleChange(e);
            }}
          />
        </Layout.Stack>
      </Card.Section>
    </Card>
  );
};

export const UploadScreen = () => {
  const { navigate, getLocation } = useRouter();
  const location = getLocation();
  const { UpdatePageTitle } = PageTitleService();
  const { UpdateBreadcrumb } = BreadcrumbService();
  const baseUrl = resolveRouteToolApiBase();

  // Returns Authorization header using the token stored by MsalService after login.
  // No credentials mode needed — Bearer token auth does not use browser-managed cookies.
  const getAuthHeaders = (): HeadersInit => {
    const token = CookieService.GetCookie('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const form = useForm({ defaultValues: { entityName: '' } });
  const [entityOptions, setEntityOptions] = useState<EntityOption[]>([]);
  const [entitiesError, setEntitiesError] = useState('');
  const [selectedEntity, setSelectedEntity] = useState('');

  const [clinicFile, setClinicFile] = useState<FileUploadState>({
    file: null,
    error: '',
    uploadedFileName: '',
  });
  const [warehouseFile, setWarehouseFile] = useState<FileUploadState>({
    file: null,
    error: '',
    uploadedFileName: '',
  });
  const [remoteUserFile, setRemoteUserFile] = useState<FileUploadState>({
    file: null,
    error: '',
    uploadedFileName: '',
  });

  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingMessage, setProcessingMessage] = useState('');

  const PRELOAD_TABLE_PATHS = ['/site-clusters', '/warehouse-clusters', '/user-sites', '/site-to-site'];
  const PRELOAD_MAP_PATHS = ['/user', '/clustered'];
  const ROUTE_TOOL_PRELOAD_KEY = 'routeToolPreload';

  /**
   * Polls /api/entities/{entity}/status until blobs.all_complete === true.
   * Table and map APIs must NOT be called before this resolves.
   */
  const pollUntilAllBlobsComplete = async (
    acquiredEntity: string,
    onStatusUpdate: (msg: string) => void,
    maxWaitMs = 600_000,
    intervalMs = 3_000,
  ): Promise<void> => {
    const url = buildApiUrl(baseUrl, `/api/entities/${encodeURIComponent(acquiredEntity)}/status`);
    const startedAt = Date.now();
    const deadline = startedAt + maxWaitMs;
    while (Date.now() < deadline) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await fetch(url, { headers: getAuthHeaders() });
        if (response.status === 404 || response.status === 401 || response.status === 403) return;
        if (response.ok) {
          let body: any;
          try { body = await response.json(); } catch { return; }
          // Proceed once table-related blobs are ready.
          // all_complete is excluded — map outputs are loaded separately.
          const blobs = body?.blobs ?? {};
          const tablesBlobsReady =
            blobs.site_clusters === true &&
            blobs.site_travel === true &&
            blobs.user_sites === true &&
            blobs.warehouse_clusters === true;
          if (tablesBlobsReady) return;
          const elapsed = Math.round((Date.now() - startedAt) / 1000);
          const stage: string = body?.job?.stage || body?.stage || '';
          const stageLabel = stage ? ` — ${stage}` : '';
          onStatusUpdate(`Processing files${stageLabel} (${elapsed}s)… Please wait.`);
        }
      } catch {
        return; // network error — proceed optimistically
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise<void>((resolve) => { setTimeout(resolve, intervalMs); });
    }
    // timeout — proceed anyway
  };

  const preloadResultsData = async (acquiredEntity: string) => {
    const tablePayloads: Record<string, any> = {};
    const mapPayloads: Record<string, string> = {};

    // Read upload flags to skip preloading endpoints for files not uploaded.
    let hasWarehouse = true;
    let hasUser = true;
    try {
      const flagsRaw = window.sessionStorage.getItem(ROUTE_TOOL_UPLOAD_FLAGS_KEY);
      if (flagsRaw) {
        const flags = JSON.parse(flagsRaw);
        if (flags.entity === acquiredEntity) {
          hasWarehouse = flags.has_warehouse !== false;
          hasUser = flags.has_user !== false;
        }
      }
    } catch {
      // default to true
    }

    const activePaths = {
      tables: PRELOAD_TABLE_PATHS.filter((path) => {
        if (path === '/warehouse-clusters' && !hasWarehouse) return false;
        if (path === '/user-sites' && !hasUser) return false;
        return true;
      }),
      maps: PRELOAD_MAP_PATHS.filter((path) => {
        if (path === '/user' && !hasUser) return false;
        return true;
      }),
    };

    await Promise.allSettled([
      ...activePaths.tables.map(async (path) => {
        try {
          const q = `page=1&page_size=10&sort_dir=asc`;
          const r = await fetch(
            buildApiUrl(baseUrl, `/api/tables/${encodeURIComponent(acquiredEntity)}${path}?${q}`),
            { headers: getAuthHeaders() }
          );
          if (r.ok) {
            const ct = r.headers.get('content-type') || '';
            tablePayloads[path] = ct.includes('application/json')
              ? await r.json().catch(() => null)
              : await r.text().catch(() => null);
          }
        } catch {
          // non-critical — ResultsScreen will refetch if missing
        }
      }),
      ...activePaths.maps.map(async (path) => {
        try {
          const r = await fetch(
            buildApiUrl(baseUrl, `/api/maps/${encodeURIComponent(acquiredEntity)}${path}`),
            { headers: getAuthHeaders() }
          );
          if (r.ok) {
            mapPayloads[path] = await r.text().catch(() => '');
          }
        } catch {
          // non-critical
        }
      }),
    ]);

    window.sessionStorage.setItem(
      ROUTE_TOOL_PRELOAD_KEY,
      JSON.stringify({ entity: acquiredEntity, tablePayloads, mapPayloads })
    );
  };

  useEffect(() => {
    if (!isProcessing) {
      if (uploadProgress > 0) {
        setUploadProgress(100);
        const timer = setTimeout(() => setUploadProgress(0), 600);
        return () => clearTimeout(timer);
      }
      return undefined;
    }

    setUploadProgress(5);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        const increment = Math.random() * 10 + 3;
        const next = prev + increment;
        return next >= 90 ? 90 : next;
      });
    }, 600);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing]);
  const isRequiredClinicFilePresent = Boolean(
    clinicFile.file || clinicFile.uploadedFileName
  );
  const hasAnyFileError = Boolean(
    (clinicFile.file === null && clinicFile.error) ||
    (warehouseFile.file === null && warehouseFile.error) ||
    (remoteUserFile.file === null && remoteUserFile.error)
  );
  const bulkUploadInputRef = useRef<HTMLInputElement>(null);

  const hasTableData = (payload: any): boolean => {
    if (!payload) {
      return false;
    }

    if (Array.isArray(payload)) {
      return payload.length > 0;
    }

    if (Array.isArray(payload?.rows)) {
      return payload.rows.length > 0;
    }

    if (Array.isArray(payload?.data?.rows)) {
      return payload.data.rows.length > 0;
    }

    if (Array.isArray(payload?.table?.rows)) {
      return payload.table.rows.length > 0;
    }

    const firstArray = Object.values(payload).find((value) =>
      Array.isArray(value)
    );
    return Array.isArray(firstArray) && firstArray.length > 0;
  };

  const detectUploadedFilesFromServer = async (entityName: string) => {
    if (!entityName) {
      return;
    }

    const tableChecks: Array<{
      endpoint: string;
      applyUploaded: () => void;
    }> = [
      {
        endpoint: `/api/tables/${encodeURIComponent(entityName)}/site-clusters`,
        applyUploaded: () => {
          setClinicFile((prev) => ({
            ...prev,
            uploadedFileName: prev.uploadedFileName || 'Already uploaded',
            error: '',
          }));
        },
      },
      {
        endpoint: `/api/tables/${encodeURIComponent(entityName)}/warehouse-clusters`,
        applyUploaded: () => {
          setWarehouseFile((prev) => ({
            ...prev,
            uploadedFileName: prev.uploadedFileName || 'Already uploaded',
            error: '',
          }));
        },
      },
      {
        endpoint: `/api/tables/${encodeURIComponent(entityName)}/user-sites`,
        applyUploaded: () => {
          setRemoteUserFile((prev) => ({
            ...prev,
            uploadedFileName: prev.uploadedFileName || 'Already uploaded',
            error: '',
          }));
        },
      },
    ];

    await Promise.all(
      tableChecks.map(async (check) => {
        try {
          const response = await fetch(buildApiUrl(baseUrl, check.endpoint), {
            headers: getAuthHeaders(),
          });

          if (!response.ok) {
            return;
          }

          // If the endpoint is reachable for this entity, treat file as already uploaded.
          check.applyUploaded();

          const contentType = response.headers.get('content-type') || '';
          const payload = contentType.includes('application/json')
            ? await response.json().catch(() => ({}))
            : await response.text().catch(() => '');

          if (typeof payload === 'string') {
            return;
          }

          if (hasTableData(payload)) {
            check.applyUploaded();
          }
        } catch {
          // Ignore detection errors and keep current UI state.
        }
      })
    );
  };

  const buildValidatedFileState = async (
    file: File | null,
    expectedKind: UploadFileKind
  ): Promise<FileUploadState> => {
    if (!file) {
      return { file: null, error: '', uploadedFileName: '' };
    }

    const validationError = await validateUploadFile(file);

    if (validationError) {
      return { file: null, error: validationError, uploadedFileName: '' };
    }

    const kindValidationError = validateFileKindByName(file, expectedKind);
    if (kindValidationError) {
      return { file: null, error: kindValidationError, uploadedFileName: '' };
    }

    const addressError = await checkAddressMissing(file);
    if (addressError) {
      return { file: null, error: addressError, uploadedFileName: '' };
    }

    return { file, error: '', uploadedFileName: file.name };
  };

  const uploadFilesNow = async (
    acquiredEntity: string,
    clinic: File | null,
    warehouse: File | null,
    remoteUser: File | null
  ): Promise<boolean> => {
    if (!acquiredEntity || !clinic) {
      return false;
    }

    setIsProcessing(true);

    try {
      const formData = new FormData();
      formData.append('acquired_entity', acquiredEntity);
      formData.append('confirm_overwrite', 'true');
      formData.append('general', clinic as Blob);

      if (warehouse) {
        formData.append('warehouse', warehouse);
      }

      if (remoteUser) {
        formData.append('user', remoteUser);
      }

      const response = await fetch(buildApiUrl(baseUrl, '/api/upload'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message =
          result?.message ||
          result?.error ||
          `Upload failed with ${response.status}`;
        setClinicFile((prev) => ({ ...prev, error: String(message) }));
        return false;
      }

      setClinicFile((prev) => ({
        ...prev,
        error: '',
        uploadedFileName: clinic?.name || prev.uploadedFileName,
      }));
      if (warehouse) {
        setWarehouseFile((prev) => ({
          ...prev,
          error: '',
          uploadedFileName: warehouse.name,
        }));
      }
      if (remoteUser) {
        setRemoteUserFile((prev) => ({
          ...prev,
          error: '',
          uploadedFileName: remoteUser.name,
        }));
      }

      appendRouteToolHistoryRecord(
        createHistoryRecordFromUpload(acquiredEntity, result)
      );

      // Persist upload flags so ResultsScreen can skip endpoints for files not uploaded.
      // has_warehouse / has_user come from the POST /upload 202 response.
      try {
        window.sessionStorage.setItem(
          ROUTE_TOOL_UPLOAD_FLAGS_KEY,
          JSON.stringify({
            entity: acquiredEntity,
            has_warehouse: Boolean(result?.has_warehouse),
            has_user: Boolean(result?.has_user),
          })
        );
      } catch {
        // non-critical — ResultsScreen defaults both flags to true when absent
      }

      return true;
    } catch (error: any) {
      setClinicFile((prev) => ({
        ...prev,
        error: error?.message || 'Unable to upload files. Please try again.',
      }));
      return false;
    } finally {
      // isProcessing is intentionally NOT cleared here so the overlay
      // stays visible during the subsequent preloadResultsData phase.
      // handleSubmit is responsible for clearing it on failure.
    }
  };

  const handleClinicFileChange = async (file: File | null) => {
    const validatedState = await buildValidatedFileState(file, 'clinic');
    setClinicFile(validatedState);
  };

  const handleWarehouseFileChange = async (file: File | null) => {
    const validatedState = await buildValidatedFileState(file, 'warehouse');
    setWarehouseFile(validatedState);
  };

  const handleRemoteUserFileChange = async (file: File | null) => {
    const validatedState = await buildValidatedFileState(file, 'remoteUser');
    setRemoteUserFile(validatedState);
  };

  const applyBulkSelectedFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }

    const selectedFiles = Array.from(files);

    const isClinicEmpty = !clinicFile.file && !clinicFile.uploadedFileName;
    const isWarehouseEmpty =
      !warehouseFile.file && !warehouseFile.uploadedFileName;
    const isRemoteUserEmpty =
      !remoteUserFile.file && !remoteUserFile.uploadedFileName;

    let clinicMatch: File | null = null;
    let warehouseMatch: File | null = null;
    let remoteUserMatch: File | null = null;
    const unmatched: File[] = [];

    for (const file of selectedFiles) {
      const name = file.name.toLowerCase();
      if (!clinicMatch && (name.includes('site') || name.includes('clinic'))) {
        clinicMatch = file;
      } else if (!warehouseMatch && name.includes('warehouse')) {
        warehouseMatch = file;
      } else if (
        !remoteUserMatch &&
        (name.includes('user') || name.includes('remote'))
      ) {
        remoteUserMatch = file;
      } else {
        unmatched.push(file);
      }
    }

    const unmatchedQueue = [...unmatched];
    if (!clinicMatch && isClinicEmpty && unmatchedQueue.length > 0) {
      clinicMatch = unmatchedQueue.shift()!;
    }
    if (!warehouseMatch && isWarehouseEmpty && unmatchedQueue.length > 0) {
      warehouseMatch = unmatchedQueue.shift()!;
    }
    if (!remoteUserMatch && isRemoteUserEmpty && unmatchedQueue.length > 0) {
      remoteUserMatch = unmatchedQueue.shift()!;
    }

    if (clinicMatch && isClinicEmpty) {
      await handleClinicFileChange(clinicMatch);
    }
    if (warehouseMatch && isWarehouseEmpty) {
      await handleWarehouseFileChange(warehouseMatch);
    }
    if (remoteUserMatch && isRemoteUserEmpty) {
      await handleRemoteUserFileChange(remoteUserMatch);
    }

    if (unmatchedQueue.length > 0) {
      const ignored = unmatchedQueue.map((file) => file.name).join(', ');
      setClinicFile((prev) => ({
        ...prev,
        error: `Extra files ignored (all slots filled): ${ignored}`,
      }));
    }
  };

  const removeAllFiles = () => {
    setClinicFile({ file: null, error: '', uploadedFileName: '' });
    setWarehouseFile({ file: null, error: '', uploadedFileName: '' });
    setRemoteUserFile({ file: null, error: '', uploadedFileName: '' });
    window.sessionStorage.removeItem(UPLOAD_STATE_STORAGE_KEY);
  };

  const getFileNameForKind = (fileKind: UploadFileKind) =>
    fileKind === 'clinic'
      ? clinicFile.uploadedFileName
      : fileKind === 'warehouse'
        ? warehouseFile.uploadedFileName
        : remoteUserFile.uploadedFileName;

  const loadEntities = async () => {
    setEntitiesError('');

    try {
      const endpointCandidates = ['/api/entities', '/api/entities/summary'];
      let data: any = null;
      let lastStatus: number | null = null;

      const token = CookieService.GetCookie('authToken');
      for (const endpoint of endpointCandidates) {
        const response = await fetch(buildApiUrl(baseUrl, endpoint), {
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          lastStatus = response.status;
          continue;
        }

        data = await response.json();
        break;
      }

      if (!data) {
        throw new Error(
          `Entities failed${lastStatus ? ` with ${lastStatus}` : ''}`
        );
      }

      const entities = Array.isArray(data)
        ? data
        : Array.isArray(data?.entities)
          ? data.entities
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.items)
              ? data.items
              : [];

      const options = entities
        .map((item: any) => {
          if (typeof item === 'string') {
            const value = item.trim();
            return value ? { value, label: value } : null;
          }

          if (item && typeof item === 'object') {
            const value = String(
              item.entity || item.name || item.id || ''
            ).trim();
            if (!value) {
              return null;
            }
            return { value, label: value };
          }

          return null;
        })
        .filter((item: EntityOption | null): item is EntityOption =>
          Boolean(item)
        );

      if (options.length === 0) {
        setEntityOptions([]);
        setEntitiesError('Entities API returned an empty list.');
        return;
      }

      setEntityOptions(options);
    } catch (error: any) {
      setEntityOptions([]);
      setEntitiesError(
        error?.message || 'Unable to load acquired entities API.'
      );
    }
  };

  useEffect(() => {
    (async () => {
      await UpdatePageTitle(Title);
      await UpdateBreadcrumb(Breadcrumbs);
      await loadEntities();
    })();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location?.search || '');
    const step = params.get('step');
    const entityFromQuery = params.get('entity');
    const isFromResults = params.get('fromResults') === '1';

    if (step === '2') {
      setCurrentStep(2);
    }

    if (entityFromQuery) {
      const decoded = decodeURIComponent(entityFromQuery);
      form.setValue('entityName', decoded);
      setSelectedEntity(decoded);
    }

    // Fresh upload entry should not reuse prior uploaded-file indicators.
    if (!isFromResults) {
      setClinicFile((prev) => ({ ...prev, uploadedFileName: '' }));
      setWarehouseFile((prev) => ({ ...prev, uploadedFileName: '' }));
      setRemoteUserFile((prev) => ({ ...prev, uploadedFileName: '' }));
    }
  }, [location?.search]);

  useEffect(() => {
    const params = new URLSearchParams(location?.search || '');
    const isFromResults = params.get('fromResults') === '1';
    if (!isFromResults) {
      window.sessionStorage.removeItem(UPLOAD_STATE_STORAGE_KEY);
      return;
    }

    const hydrateIfMissing = async (entityName: string) => {
      if (!entityName) {
        return;
      }

      const hasAllUploadedNames =
        Boolean(clinicFile.uploadedFileName) &&
        Boolean(warehouseFile.uploadedFileName) &&
        Boolean(remoteUserFile.uploadedFileName);

      if (!hasAllUploadedNames) {
        await detectUploadedFilesFromServer(entityName);
      }
    };

    const persistedRaw = window.sessionStorage.getItem(
      UPLOAD_STATE_STORAGE_KEY
    );
    if (!persistedRaw) {
      const queryEntity = decodeURIComponent(params.get('entity') || '').trim();
      if (queryEntity) {
        void hydrateIfMissing(queryEntity);
      }
      return;
    }

    try {
      const persisted = JSON.parse(persistedRaw) as PersistedUploadState;
      const queryEntity = decodeURIComponent(params.get('entity') || '').trim();
      if (
        queryEntity &&
        persisted.entityName &&
        queryEntity !== persisted.entityName
      ) {
        // Do not apply stale uploaded state to a different entity.
        return;
      }

      if (persisted.entityName) {
        form.setValue('entityName', persisted.entityName);
        setSelectedEntity(persisted.entityName);
      }
      if (persisted.clinicFileName) {
        setClinicFile((prev) => ({
          ...prev,
          uploadedFileName: persisted.clinicFileName,
        }));
      }
      if (persisted.warehouseFileName) {
        setWarehouseFile((prev) => ({
          ...prev,
          uploadedFileName: persisted.warehouseFileName,
        }));
      }
      if (persisted.remoteUserFileName) {
        setRemoteUserFile((prev) => ({
          ...prev,
          uploadedFileName: persisted.remoteUserFileName,
        }));
      }

      const sourceEntity = (
        persisted.entityName || decodeURIComponent(params.get('entity') || '')
      ).trim();
      if (sourceEntity) {
        void hydrateIfMissing(sourceEntity);
      }
    } catch {
      window.sessionStorage.removeItem(UPLOAD_STATE_STORAGE_KEY);
    }
  }, [
    location?.search,
    clinicFile.uploadedFileName,
    warehouseFile.uploadedFileName,
    remoteUserFile.uploadedFileName,
  ]);

  useEffect(() => {
    const entityName = String(form.getValues().entityName || '').trim();
    const payload: PersistedUploadState = {
      entityName,
      clinicFileName: clinicFile.uploadedFileName,
      warehouseFileName: warehouseFile.uploadedFileName,
      remoteUserFileName: remoteUserFile.uploadedFileName,
    };

    window.sessionStorage.setItem(
      UPLOAD_STATE_STORAGE_KEY,
      JSON.stringify(payload)
    );
  }, [
    clinicFile.uploadedFileName,
    warehouseFile.uploadedFileName,
    remoteUserFile.uploadedFileName,
  ]);

  const handleSubmit = async (values: { entityName: string }) => {
    if (isProcessing) {
      return;
    }

    let hasError = false;
    // Use selectedEntity as fallback — the native <select> is not a registered form field
    // so values.entityName may be empty even when an entity is visually selected.
    const acquiredEntity = (values.entityName || selectedEntity).trim();
    const hasClinicFile = Boolean(
      clinicFile.file || clinicFile.uploadedFileName
    );

    if (!acquiredEntity) {
      form.setError('entityName', { message: 'Acquired Entity is required' });
      hasError = true;
    }

    if (!hasClinicFile) {
      setClinicFile((prev) => ({
        ...prev,
        error: 'Clinic Site file is required',
      }));
      hasError = true;
    }

    if (warehouseFile.file === null && warehouseFile.error) {
      hasError = true;
    }

    if (remoteUserFile.file === null && remoteUserFile.error) {
      hasError = true;
    }

    if (hasError) {
      return;
    }

    if (
      !clinicFile.file &&
      !warehouseFile.file &&
      !remoteUserFile.file &&
      hasClinicFile
    ) {
      navigate('/');
      return;
    }

    // Write processing state and navigate to Dashboard immediately — before
    // the upload resolves — so the user is not blocked waiting on the network.
    try {
      window.sessionStorage.setItem(
        UPLOAD_PROCESSING_KEY,
        JSON.stringify({ entity: acquiredEntity, startedAt: Date.now() }),
      );
      window.dispatchEvent(
        new CustomEvent('routeToolUploadStarted', {
          detail: { entity: acquiredEntity, startedAt: Date.now() },
        }),
      );
    } catch {
      // non-critical
    }

    navigate('/');

    // Fire the upload in the background after navigating away.
    uploadFilesNow(
      acquiredEntity,
      clinicFile.file,
      warehouseFile.file,
      remoteUserFile.file
    );
  };

  const handleStepOneNext = () => {
    const values = form.getValues();

    if (!values.entityName?.trim()) {
      form.setError('entityName', { message: 'Acquired Entity is required' });
      return;
    }

    setCurrentStep(2);
  };

  const handleCancel = () => {
    navigate('/');
  };

  const downloadTemplate = (fileKind: UploadFileKind) => {
    const templateKey = fileKind === 'remoteUser' ? 'user' : fileKind;

    void (async () => {
      try {
        const response = await fetch(
          buildApiUrl(baseUrl, `/api/templates/${templateKey}`),
          {
            headers: getAuthHeaders(),
          }
        );

        if (!response.ok) {
          throw new Error(`Template download failed with ${response.status}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${templateKey}_template.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (error: any) {
        setClinicFile((prev) => ({
          ...prev,
          error: error?.message || 'Unable to download template.',
        }));
      }
    })();
  };

  const buildLocalPreviewFromFile = async (file: File | null) => {
    if (!file) {
      return { headers: [], rows: [] };
    }

    const lowerFileName = file.name.toLowerCase();

    if (lowerFileName.endsWith('.xlsx') || lowerFileName.endsWith('.xls')) {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) {
        return { headers: [], rows: [] };
      }
      const sheet = workbook.Sheets[sheetName];
      const data: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as string[][];
      if (data.length === 0) {
        return { headers: [], rows: [] };
      }
      const headers = data[0].map((cell) => String(cell ?? ''));
      const rows = data.slice(1, 11).map((row) =>
        headers.map((_, i) => String(row[i] ?? ''))
      );
      return { headers, rows };
    }

    if (lowerFileName.endsWith('.csv')) {
      const text = await file.text();
      const lines = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      if (lines.length === 0) {
        return { headers: [], rows: [] };
      }
      const headers = parseCsvRow(lines[0]);
      const rows = lines.slice(1, 11).map((line) => parseCsvRow(line));
      return { headers, rows };
    }

    throw new Error('Unsupported file type for local preview.');
  };

  const loadFilePreview = async (fileKind: UploadFileKind) => {
    const previewTypeCandidates =
      fileKind === 'clinic'
        ? ['general', 'clinic', 'site', 'sites']
        : fileKind === 'remoteUser'
          ? ['user', 'users', 'remote_user', 'remote-user', 'remoteUser']
          : ['warehouse', 'warehouses', 'warehouse_file'];
    const selectedEntity = String(form.getValues().entityName || '').trim();

    if (!selectedEntity) {
      throw new Error(
        'Please select an Acquired Entity before previewing files.'
      );
    }

    const entityKeys = ['entity_name', 'entity', 'acquired_entity'];
    const typeKeys = ['file_type', 'type', 'file_kind', 'preview_type'];
    const previewUrlSet = new Set<string>();

    for (const previewType of previewTypeCandidates) {
      previewUrlSet.add(
        buildApiUrl(
          baseUrl,
          `/api/entities/${encodeURIComponent(selectedEntity)}/preview/${previewType}`
        )
      );
      previewUrlSet.add(
        buildApiUrl(baseUrl, `/api/entities/current/preview/${previewType}`)
      );
    }

    for (const entityKey of entityKeys) {
      for (const typeKey of typeKeys) {
        for (const previewType of previewTypeCandidates) {
          const params = new URLSearchParams();
          params.set(entityKey, selectedEntity);
          params.set(typeKey, previewType);
          previewUrlSet.add(
            buildApiUrl(baseUrl, `/api/entities/preview?${params.toString()}`)
          );
        }
      }
    }

    for (const typeKey of typeKeys) {
      for (const previewType of previewTypeCandidates) {
        const params = new URLSearchParams();
        params.set(typeKey, previewType);
        previewUrlSet.add(
          buildApiUrl(
            baseUrl,
            `/api/entities/current/preview?${params.toString()}`
          )
        );
        previewUrlSet.add(
          buildApiUrl(baseUrl, `/api/entities/preview?${params.toString()}`)
        );
      }
    }

    const previewUrls = Array.from(previewUrlSet);
    const localFile =
      fileKind === 'clinic'
        ? clinicFile.file
        : fileKind === 'warehouse'
          ? warehouseFile.file
          : remoteUserFile.file;

    // If a local file has been selected, use it directly — avoids hitting the backend
    // for entities that don't have blob files yet (e.g. newly created entities).
    if (localFile) {
      return buildLocalPreviewFromFile(localFile);
    }

    let parsed: any = {};
    let lastError = '';
    let success = false;

    for (const url of previewUrls) {
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      parsed = await response.json().catch(() => ({}));
      if (response.ok) {
        success = true;
        break;
      }

      // Keep trying if the backend is using a different route style or file doesn't exist yet.
      lastError =
        parsed?.error || `Preview endpoint not found (${response.status})`;
      continue;
    }

    if (!success) {
      if (localFile || getFileNameForKind(fileKind)) {
        return buildLocalPreviewFromFile(localFile);
      }

      throw new Error(
        lastError || 'Unable to load preview from backend endpoint.'
      );
    }

    if (parsed?.missing_optional_file) {
      if (localFile || getFileNameForKind(fileKind)) {
        return buildLocalPreviewFromFile(localFile);
      }

      throw new Error(
        parsed?.message ||
          'Optional file has not been uploaded for the selected entity yet.'
      );
    }

    const headers = Array.isArray(parsed?.columns)
      ? parsed.columns.map((item: any) => String(item))
      : [];
    const rawRows = Array.isArray(parsed?.rows) ? parsed.rows : [];
    const rows = rawRows.slice(0, 10).map((row: any) =>
      headers.map((header) => {
        const value = row?.[header];
        return value === null || value === undefined ? '' : String(value);
      })
    );

    if (localFile && headers.length === 0 && rows.length === 0) {
      return buildLocalPreviewFromFile(localFile);
    }

    return { headers, rows };
  };

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <RouteToolStepper currentStep={currentStep} uploadOnly />

      {isProcessing && (
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <Label size="sm" color="$gray6">Uploading files…</Label>
            <span style={{ fontSize: '12px', color: '#196ecf', fontWeight: 700 }}>{Math.round(uploadProgress)}%</span>
          </div>
          <div style={{ width: '100%', height: '6px', backgroundColor: '#e8edf4', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${uploadProgress}%`, backgroundColor: '#196ecf', borderRadius: '4px', transition: 'width 0.5s ease' }} />
          </div>
        </div>
      )}

      <FormProvider state={form} onSubmit={handleSubmit}>
        {currentStep === 1 ? (
          <Card>
            <Card.Section>
              <Grid>
                <Grid.Col span={{ xs: 12, md: 6 }}>
                  <Label
                    size="sm"
                    color="$gray7"
                    css={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: 700,
                    }}
                  >
                    Acquired Entity <span style={{ color: '#d92d20' }}>*</span>
                  </Label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                    <select
                      value={selectedEntity}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSelectedEntity(value);
                        form.setValue('entityName', value);
                      }}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        height: '40px',
                        border: '1px solid #d3dce8',
                        borderRadius: '4px',
                        padding: '0 12px',
                        backgroundColor: '#fff',
                        fontSize: '14px',
                      }}
                    >
                      <option value="">Select acquired entity</option>
                      {entityOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {entitiesError ? (
                    <Label
                      size="sm"
                      color="$error1"
                      css={{ display: 'block', marginTop: '8px' }}
                    >
                      {entitiesError}
                    </Label>
                  ) : null}
                </Grid.Col>
              </Grid>
            </Card.Section>
          </Card>
        ) : null}

        {currentStep === 2 ? (
          <>
            <Card
              css={{
                background: '#ffffff',
                border: '1px solid #d9e0eb',
                boxShadow: '0 2px 8px rgba(20, 40, 80, 0.06)',
              }}
            >
              <Card.Section css={{ backgroundColor: '#ffffff', padding: '12px' }}>
                <Box
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    marginBottom: '10px',
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Layout.Group space={8} css={{ marginTop: '0' }}>
                    <Button
                      size="$sm"
                      variant="outline"
                      onClick={() => bulkUploadInputRef.current?.click()}
                    >
                      <IconSymbol icon="upload_file" size={16} />
                      Upload all files
                    </Button>

                    <Button
                      size="$sm"
                      variant="destructive"
                      onClick={removeAllFiles}
                    >
                      <IconSymbol icon="delete" size={16} />
                      Remove all files
                    </Button>
                  </Layout.Group>
                </Box>

                <input
                  ref={bulkUploadInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  multiple
                  style={{ display: 'none' }}
                  onChange={async (event) => {
                    await applyBulkSelectedFiles(event.target.files);
                    if (bulkUploadInputRef.current) {
                      bulkUploadInputRef.current.value = '';
                    }
                  }}
                />

                <Grid>
                  <Grid.Col span={{ xs: 12, md: 4 }}>
                    <FileUploadBox
                      label="Clinic Site File"
                      required
                      fileKind="clinic"
                      onPreview={loadFilePreview}
                      onDownloadTemplate={downloadTemplate}
                      fileState={clinicFile}
                      onFileChange={handleClinicFileChange}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 4 }}>
                    <FileUploadBox
                      label="Warehouse File"
                      fileKind="warehouse"
                      onPreview={loadFilePreview}
                      onDownloadTemplate={downloadTemplate}
                      fileState={warehouseFile}
                      onFileChange={handleWarehouseFileChange}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 4 }}>
                    <FileUploadBox
                      label="Remote User File"
                      fileKind="remoteUser"
                      onPreview={loadFilePreview}
                      onDownloadTemplate={downloadTemplate}
                      fileState={remoteUserFile}
                      onFileChange={handleRemoteUserFileChange}
                    />
                  </Grid.Col>
                </Grid>
              </Card.Section>
            </Card>
          </>
        ) : null}

        <Layout.Group
          space={16}
          css={{ justifyContent: 'flex-end', marginTop: 'auto' }}
        >
          {currentStep === 1 ? (
            <>
              <Button
                onClick={handleStepOneNext}
                isDisabled={!selectedEntity.trim()}
              >
                Next
                <IconSymbol icon="arrow_forward" size={16} />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setCurrentStep(1)}>
                <IconSymbol icon="arrow_back" size={16} />
                Back
              </Button>
              <Button variant="outline" onClick={() => {
                setSelectedEntity('');
                setClinicFile({ file: null, error: '', uploadedFileName: '' });
                setWarehouseFile({ file: null, error: '', uploadedFileName: '' });
                setRemoteUserFile({ file: null, error: '', uploadedFileName: '' });
                setCurrentStep(1);
              }}>
                <IconSymbol icon="close" size={16} />
                Close
              </Button>
              <Button
                type="submit"
                isDisabled={!isRequiredClinicFilePresent || hasAnyFileError || isProcessing}
                isLoading={isProcessing}
                ariaLoadingLabel="Uploading files..."
              >
                Upload
                <IconSymbol icon="arrow_forward" size={16} />
              </Button>
            </>
          )}
        </Layout.Group>
      </FormProvider>
    </Box>
  );
};
